namespace $ {

	export type $shm_hitalama_file_attach_pb_meta = { title?: string, map?: string, widget?: string, field?: string }

	export class $shm_hitalama_file_attach_pb_store extends $mol_object2 {
		static collection() { return 'hitalama_form_files' }
		static secret_header() { return 'X-Hitalama-File-Secret' }
		static record_url( id = '' ) {
			return `${ this.$.$shm_pb.url() }/api/collections/${ this.collection() }/records${ id ? `/${ encodeURIComponent( id ) }` : '' }`
		}
		static secret_make() {
			const bytes = new Uint8Array( 32 )
			this.$.$mol_dom_context.crypto.getRandomValues( bytes )
			return Array.from( bytes, byte => byte.toString( 16 ).padStart( 2, '0' ) ).join( '' )
		}
		static async json( response: Response ) {
			const text = await response.text()
			const data = text ? JSON.parse( text ) : null
			if( !response.ok ) throw new Error( String( data?.message || response.statusText || 'PocketBase request failed' ) )
			return data as $shm_pocketbase_record
		}
		static async record_create( secret: string, meta: $shm_hitalama_file_attach_pb_meta ) {
			return this.json( await this.$.$mol_dom_context.fetch( this.record_url(), {
				method: 'POST', headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ delete_secret: secret, status: 'uploading', ... meta }),
			} ) )
		}
		static async patch( record: $shm_pocketbase_record, secret: string, file: File, signal: AbortSignal ) {
			const form = new FormData()
			form.set( 'file', file )
			form.set( 'title', file.name )
			form.set( 'status', 'ready' )
			return this.json( await this.$.$mol_dom_context.fetch( this.record_url( record.id ), {
				method: 'PATCH', headers: { [ this.secret_header() ]: secret }, body: form, signal,
			} ) )
		}
		static value_make( record: $shm_pocketbase_record, secret: string ) {
			const file = this.$.$shm_pb.record_files( record, 'file' )[0]
			if( !file ) throw new Error( 'PocketBase did not return the uploaded file' )
			const uri = new URL( this.$.$shm_pb.file_uri( record, this.collection(), file ) )
			uri.hash = new URLSearchParams({ hitalama_file_secret: secret }).toString()
			return uri.toString()
		}
		static value_parse( value: string ) {
			if( !value ) return null
			let uri: URL
			try {
				uri = new URL( value )
			} catch {
				return null
			}
			const parts = uri.pathname.split( '/' ).filter( Boolean )
			const index = parts.indexOf( 'files' )
			if( index < 0 || parts[ index + 1 ] !== this.collection() ) return null
			const id = decodeURIComponent( parts[ index + 2 ] ?? '' )
			const secret = new URLSearchParams( uri.hash.slice( 1 ) ).get( 'hitalama_file_secret' ) ?? ''
			return id && secret ? { id, secret, uri } : null
		}
		static async delete_record( id: string, secret: string ) {
			const response = await this.$.$mol_dom_context.fetch( this.record_url( id ), {
				method: 'DELETE', headers: { [ this.secret_header() ]: secret },
			} )
			if( response.ok || response.status === 404 ) return
			await this.json( response )
		}
		static async delete_value( value: string ) {
			const parsed = this.value_parse( value )
			if( parsed ) await this.delete_record( parsed.id, parsed.secret )
		}
		static async upload( file: File, signal: AbortSignal, meta: $shm_hitalama_file_attach_pb_meta ) {
			const secret = this.secret_make()
			let record: $shm_pocketbase_record | null = null
			try {
				record = await this.record_create( secret, { ... meta, title: file.name } )
				if( signal.aborted ) throw new DOMException( 'Upload cancelled', 'AbortError' )
				const uploaded = await this.patch( record, secret, file, signal )
				if( signal.aborted ) throw new DOMException( 'Upload cancelled', 'AbortError' )
				return this.value_make( uploaded, secret )
			} catch( error ) {
				if( record ) await this.delete_record( record.id, secret ).catch( ()=> null )
				throw error
			}
		}
	}
}

namespace $.$$ {
	export class $shm_hitalama_file_attach_pb extends $.$shm_hitalama_file_attach_pb {
		_upload_serial = 0
		_upload_controller: AbortController | null = null
		@ $mol_action
		form_reset() {
			this._upload_controller?.abort()
			++ this._upload_serial
			this._upload_controller = null
			super.files( [] )
			this.uploading( false )
			this.deleting( false )
			this.upload_message( '' )
			this.status_success( false )
		}
		@ $mol_mem
		status_success( next?: boolean ) { return next ?? false }
		status_color() {
			if( this.status_success() ) return `${ $shm_theme.success }`
			if( this.uploading() || this.deleting() ) return `${ $shm_theme.active }`
			if( this.upload_message() && !this.upload_message().includes( 'отменена' ) ) return `${ $shm_theme.error }`
			return `${ $mol_theme.shade }`
		}
		filename() {
			const selected = this.files()[0]
			if( selected ) return selected.name
			const parsed = this.$.$shm_hitalama_file_attach_pb_store.value_parse( this.value() )
			if( !parsed ) return ''
			return decodeURIComponent( parsed.uri.pathname.split( '/' ).at( -1 ) ?? '' )
		}
		cancel_visible() { return this.uploading() ? super.cancel_visible() : [] }
		delete_visible() { return this.value() && !this.uploading() ? super.delete_visible() : [] }
		files( next?: readonly File[] ): readonly File[] {
			if( next === undefined ) return super.files()
			super.files( next )
			if( next[0] ) this.upload_start( next[0] )
			return next
		}
		@ $mol_action
		upload_start( file: File ) {
			this._upload_controller?.abort()
			const controller = new AbortController()
			const serial = ++ this._upload_serial
			const previous = this.value()
			this._upload_controller = controller
			this.value( '' )
			this.uploading( true )
			this.status_success( false )
			this.upload_message( `Загрузка «${ file.name }»...` )
			this.replace( file, controller.signal, previous )
				.then( value => this.upload_done( serial, file, value ), error => this.upload_failed( serial, error ) )
				.then( ()=> this.upload_finished( serial ) )
		}
		@ $mol_action
		upload_done( serial: number, file: File, value: string ) {
			if( serial !== this._upload_serial ) return
			this.value( value )
			this.upload_message( `Загружен «${ file.name }»` )
			this.status_success( true )
		}
		@ $mol_action
		upload_failed( serial: number, error: unknown ) {
			if( serial !== this._upload_serial ) return
			super.files( [] )
			this.status_success( false )
			this.upload_message( error instanceof DOMException && error.name === 'AbortError' ? 'Загрузка отменена' : String( ( error as any )?.message || error ) )
		}
		@ $mol_action
		upload_finished( serial: number ) {
			if( serial !== this._upload_serial ) return
			this._upload_controller = null
			this.uploading( false )
		}
		async replace( file: File, signal: AbortSignal, previous: string ) {
			const value = await this.$.$shm_hitalama_file_attach_pb_store.upload( file, signal, {
				map: this.map_id(), widget: this.widget_id(), field: this.field_name(),
			} )
			if( previous && previous !== value ) {
				await this.$.$shm_hitalama_file_attach_pb_store.delete_value( previous ).catch( $mol_fail_log )
			}
			return value
		}
		@ $mol_action
		upload_cancel( next?: Event ) {
			if( next === undefined ) return null
			this._upload_controller?.abort()
			return next
		}
		@ $mol_action
		file_delete( next?: Event ) {
			if( next === undefined || this.deleting() || !this.value() ) return next ?? null
			const value = this.value()
			this.deleting( true )
			this.status_success( false )
			this.upload_message( 'Удаление файла...' )
			this.$.$shm_hitalama_file_attach_pb_store.delete_value( value )
				.then( ()=> this.delete_done( value ), error => this.delete_failed( error ) )
			return next
		}
		@ $mol_action
		delete_done( value: string ) {
			if( this.value() === value ) {
				this.value( '' )
				super.files( [] )
				this.upload_message( 'Файл удалён' )
				this.status_success( true )
			}
			this.deleting( false )
		}
		@ $mol_action
		delete_failed( error: unknown ) {
			this.upload_message( String( ( error as any )?.message || error ) )
			this.status_success( false )
			this.deleting( false )
		}
	}
}
